import { useEffect, useState } from "react";
import { CloudDownload, Eye, FileSpreadsheet, GitCompareArrows, Sparkles } from "lucide-react";
import { fetchPrRequests, PrRequestRow } from "../../../services/IacEligibilityService";

const formatAmount = (amount: number | null) =>
  amount == null
    ? "—"
    : `$${amount.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const statusModifier = (status: string) => {
  const normalized = status.toLowerCase();
  if (normalized.includes("approved") || normalized.includes("eligible")) return " iac-engine__status--approved";
  if (normalized.includes("reject") || normalized.includes("ineligible")) return " iac-engine__status--rejected";
  return "";
};

export function PrListingTab() {
  const [rows, setRows] = useState<PrRequestRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    fetchPrRequests()
      .then((data) => {
        if (isMounted) setRows(data);
      })
      .catch(() => {
        if (isMounted) setError("Unable to load PR requests.");
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <section className="iac-engine__card">
      <div className="iac-engine__enrich">
        <span className="iac-engine__enrich-label">
          <span className="iac-engine__ai-badge" aria-hidden="true">
            <Sparkles size={14} />
          </span>
          Enrich the queue before evaluation
        </span>
        <div className="iac-engine__enrich-actions">
          <button type="button" className="iac-engine__btn iac-engine__btn--primary">
            <GitCompareArrows size={15} />
            Run duplicate-spend sweep
          </button>
          <button type="button" className="iac-engine__btn iac-engine__btn--ghost">
            <CloudDownload size={15} />
            Pull contract master from Ariba
          </button>
        </div>
      </div>

      <p className="iac-engine__list-title">Incoming PRs awaiting evaluation</p>

      {isLoading && <p className="iac-engine__state" role="status">Loading PR requests...</p>}
      {error && <p className="iac-engine__state" role="status">{error}</p>}
      {!isLoading && !error && rows.length === 0 && (
        <p className="iac-engine__state" role="status">No PR requests awaiting evaluation.</p>
      )}

      {rows.length > 0 && (
        <div className="iac-engine__table-wrap">
          <table className="iac-engine__table">
            <thead>
              <tr>
                <th>PR ID</th>
                <th>Supplier</th>
                <th>Category</th>
                <th>Description</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td className="iac-engine__pr-id">{row.prId}</td>
                  <td>{row.supplier}</td>
                  <td>{row.category}</td>
                  <td>
                    <span className="iac-engine__truncate" title={row.description}>
                      {row.description}
                    </span>
                  </td>
                  <td className="iac-engine__amount">{formatAmount(row.amount)}</td>
                  <td>
                    <span className={`iac-engine__status${statusModifier(row.status)}`}>{row.status}</span>
                  </td>
                  <td>
                    <div className="iac-engine__row-actions">
                      <button type="button" className="iac-engine__icon-btn" aria-label={`Evaluate ${row.prId}`}>
                        <FileSpreadsheet size={16} />
                      </button>
                      <button type="button" className="iac-engine__icon-btn" aria-label={`View ${row.prId}`}>
                        <Eye size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
